import { useState } from 'react';
import { Sparkles, Check, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUsage?: number;
  maxUsage?: number;
  limitType?: 'ai' | 'plants';
}

export function UpgradeDialog({ 
  open, 
  onOpenChange, 
  currentUsage = 3, 
  maxUsage = 3,
  limitType = 'ai'
}: UpgradeDialogProps) {
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout');
      
      if (error) {
        throw error;
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Erro ao criar sessão de pagamento');
    } finally {
      setLoading(false);
    }
  };

  const limitMessage = limitType === 'ai' 
    ? `Você usou ${currentUsage}/${maxUsage} identificações/diagnósticos este mês.`
    : `Você tem ${currentUsage}/${maxUsage} plantas cadastradas.`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto pb-24 sm:pb-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="h-6 w-6 text-amber-500" />
            Limite do Plano Gratuito
          </DialogTitle>
          <DialogDescription className="pt-2">
            {limitMessage}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            Faça upgrade para Premium e tenha:
          </p>

          <ul className="space-y-3">
            <li className="flex items-center gap-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                <Check className="h-4 w-4 text-primary" />
              </div>
              <span className="text-sm">Identificações ilimitadas</span>
            </li>
            <li className="flex items-center gap-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                <Check className="h-4 w-4 text-primary" />
              </div>
              <span className="text-sm">Diagnósticos ilimitados</span>
            </li>
            <li className="flex items-center gap-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                <Check className="h-4 w-4 text-primary" />
              </div>
              <span className="text-sm">Plantas ilimitadas na coleção</span>
            </li>
          </ul>

          <div className="rounded-lg bg-gradient-to-r from-primary/10 to-accent/10 p-4 text-center">
            <p className="text-2xl font-bold text-foreground">
              R$ 15<span className="text-sm font-normal text-muted-foreground">/mês</span>
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Button 
            onClick={handleUpgrade} 
            disabled={loading}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Carregando...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Assinar Premium
              </>
            )}
          </Button>
          <Button 
            variant="ghost" 
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Agora não
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
